/**
 * The firm's frameworks as Linear lists initiatives: name and what it answers
 * to, lead, health, controls addressed, where its publications stand, and how
 * busy its regulators have been. One rendering for the Monitor (where a row
 * narrows the publications below it) and the Frameworks page (where a row
 * opens the framework).
 *
 * @module components/regulation/FrameworksTable
 */
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, LayersIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import type { GovernanceFramework } from "../../state/governance";
import type { RegulatoryUpdate } from "../../state/policies";
import { frameworkRollup, type FrameworkRollup, type RegimeCoverage } from "./monitor/frameworkRollup";
import {
  ActivityIcon,
  ControlsIcon,
  HEALTH,
  HealthIcon,
  LINEAR_INDIGO,
  LINEAR_YELLOW,
  PersonGlyph,
} from "./monitor/linearGlyphs";

const COLUMNS =
  "grid grid-cols-[minmax(0,1fr)_2rem_7.5rem] items-center gap-x-4 md:grid-cols-[minmax(0,1fr)_2.5rem_7.5rem_6rem_8.5rem_3.5rem]";

/** Publications per state as Linear's coloured dot counts; only states that have any. */
function PublicationDots({ states }: { readonly states: FrameworkRollup["states"] }) {
  const dots = [
    { count: states.findings, color: LINEAR_YELLOW, label: "with open findings" },
    { count: states.decide, color: "var(--color-muted-foreground)", label: "need a decision" },
    { count: states.handled, color: LINEAR_INDIGO, label: "relevant" },
  ].filter((dot) => dot.count > 0);
  if (dots.length === 0) return <span className="text-muted-foreground/60">—</span>;
  return (
    <span className="flex items-center gap-3">
      {dots.map((dot) => (
        <span key={dot.label} title={`${dot.count} ${dot.label}`} className="flex items-center gap-1.5 tabular-nums">
          <span aria-hidden className="size-2 rounded-full" style={{ backgroundColor: dot.color }} />
          {dot.count}
        </span>
      ))}
    </span>
  );
}

function FrameworkCells({
  framework,
  rollup,
}: {
  readonly framework: GovernanceFramework;
  readonly rollup: FrameworkRollup;
}) {
  const documents = framework.documents.length;
  return (
    <>
      <span className="flex min-w-0 items-center gap-3">
        <span className="flex size-5 shrink-0 items-center justify-center">
          <LayersIcon aria-hidden className="size-4 text-muted-foreground" />
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13px] font-[510] text-foreground/90">{framework.name}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {documents} {documents === 1 ? "document" : "documents"}
            {framework.regimes.length > 0 ? ` · answers to ${framework.regimes.map((regime) => regime.name).join(", ")}` : ""}
          </span>
        </span>
      </span>
      <span className="flex justify-center">
        <PersonGlyph
          size="md"
          email={framework.ownerEmail}
          title={framework.ownerEmail ? `Lead: ${framework.ownerEmail}` : "No lead"}
        />
      </span>
      <span
        title={rollup.reasons.join(" · ")}
        className="flex items-center gap-2 text-[13px] font-[510]"
        style={{ color: HEALTH[rollup.health].color }}
      >
        <HealthIcon health={rollup.health} />
        {HEALTH[rollup.health].label}
      </span>
      <span
        title={`${rollup.controls.satisfied} of ${rollup.controls.total} controls addressed`}
        className="hidden items-center gap-2 text-[13px] tabular-nums text-foreground/80 md:flex"
      >
        {rollup.controls.total > 0 ? (
          <>
            <ControlsIcon />
            {rollup.controls.satisfied}
            <span className="text-muted-foreground">/</span>
            {rollup.controls.total}
          </>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        )}
      </span>
      <span className="hidden text-[13px] text-foreground/80 md:block">
        <PublicationDots states={rollup.states} />
      </span>
      <span
        title={`${rollup.recent} ${rollup.recent === 1 ? "publication" : "publications"} in the last 30 days`}
        className="hidden justify-center md:flex"
      >
        <ActivityIcon level={rollup.activity} />
      </span>
    </>
  );
}

export function FrameworksTable({
  frameworks,
  coverage,
  publications,
  now,
  orgSegment,
  selectedId = null,
  onSelect,
  footer,
  flush = false,
}: {
  readonly frameworks: ReadonlyArray<GovernanceFramework>;
  readonly coverage: ReadonlyMap<string, RegimeCoverage>;
  readonly publications: ReadonlyArray<RegulatoryUpdate>;
  readonly now: number;
  readonly orgSegment: string;
  /** The framework the page is narrowed to, drawn as selected. */
  readonly selectedId?: string | null;
  /** Given: a row click narrows the page to that framework (again to clear). Absent: a row opens it. */
  readonly onSelect?: (framework: GovernanceFramework | null) => void;
  /** A last, quieter row — e.g. regimes that sit in no framework yet. */
  readonly footer?: ReactNode;
  /** Edge to edge, rows divided, as a group of a full-page list (the Monitor); otherwise a card. */
  readonly flush?: boolean;
}) {
  const inset = flush ? "pe-4 ps-3 sm:pe-6 sm:ps-4" : "px-3";
  const row = cn(
    "w-full text-left outline-hidden transition-colors duration-150",
    inset,
    flush ? "h-12 bg-card" : "h-14 rounded-md",
  );
  const idle = "hover:bg-foreground/[0.035] focus-visible:bg-foreground/[0.05]";
  return (
    <div className={flush ? undefined : "rounded-xl border border-border/60 bg-card p-2"}>
      <div
        className={cn(
          COLUMNS,
          "h-8 text-xs text-muted-foreground",
          inset,
          flush && "border-b border-border/50 bg-card pe-14 sm:pe-16",
        )}
      >
        <span className={flush ? "ps-8" : undefined}>Name</span>
        <span className="text-center">Lead</span>
        <span>Health</span>
        <span className="hidden md:block">Controls</span>
        <span className="hidden md:block">Publications</span>
        <span className="hidden text-center md:block">Activity</span>
      </div>
      <ul className={flush ? "divide-y divide-border/50" : undefined}>
        {frameworks.map((framework) => {
          const rollup = frameworkRollup(framework, coverage, publications, now);
          const selected = selectedId === framework.id;
          const link = { to: "/$org/compliance/frameworks/$frameworkId", params: { org: orgSegment, frameworkId: framework.id } } as const;
          return (
            <li key={framework.id} className="group relative">
              {onSelect ? (
                <>
                  <button
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onSelect(selected ? null : framework)}
                    className={cn(
                      COLUMNS,
                      row,
                      "cursor-pointer",
                      flush ? "pe-14 sm:pe-16" : "pr-10",
                      selected
                        ? flush
                          ? "bg-[color-mix(in_srgb,var(--brand-accent)_7%,transparent)]"
                          : "bg-foreground/[0.06]"
                        : idle,
                    )}
                  >
                    <FrameworkCells framework={framework} rollup={rollup} />
                  </button>
                  {selected && flush ? (
                    <span aria-hidden className="pointer-events-none absolute inset-y-0 start-0 w-0.5 bg-[var(--brand-accent)]" />
                  ) : null}
                  <Link
                    {...link}
                    aria-label={`Open ${framework.name}`}
                    title="Open framework"
                    className={cn(
                      "absolute top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-foreground/[0.06] hover:text-foreground focus-visible:opacity-100 pointer-coarse:opacity-100",
                      flush ? "end-3 sm:end-5" : "end-2",
                    )}
                  >
                    <ArrowUpRightIcon className="size-3.5" />
                  </Link>
                </>
              ) : (
                <Link {...link} className={cn(COLUMNS, row, idle)}>
                  <FrameworkCells framework={framework} rollup={rollup} />
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      {footer ? (
        <div
          className={cn(
            "text-xs text-muted-foreground",
            flush
              ? "flex min-h-9 items-center border-t border-border/50 bg-card py-2 pe-4 ps-11 sm:pe-6 sm:ps-12"
              : "mt-1 border-t border-border/50 px-3 pt-2 pb-1",
          )}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}
